import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  deleteUser,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { app } from "@lib/firebase"; // Pastikan alias '@lib/firebase' sudah benar di Vite

const auth = getAuth(app);
const db = getFirestore(app);
const googleProvider = new GoogleAuthProvider();

/**
 * 🔹 Simpan user ke Firestore
 */
const saveUserToFirestore = async (user) => {
  if (!user || !user.uid) return;

  try {
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        uid: user.uid,
        name: user.displayName || "",
        email: user.email,
        photoURL: user.photoURL || "",
        createdAt: serverTimestamp(),
        role: "user", // default role
      });
    }
  } catch (error) {
    console.error("❌ Gagal menyimpan user:", error.message);
    throw error; // Lempar error supaya bisa ditangkap di frontend
  }
};

/**
 * 🔹 Register user & kirim email verifikasi
 */
export const registerAndSendVerification = async (email, name, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Update displayName
    await updateProfile(user, { displayName: name });

    // Kirim email verifikasi
    await sendEmailVerification(user);

    // Simpan ke Firestore setelah update profile
    await saveUserToFirestore(user);

    return {
      message: "Please check your email and complete verification before logging in.",
      user,
    };
  } catch (error) {
    console.error("Error register:", error.message);
    const errorCode = error.code || "unknown_error";
    switch (errorCode) {
      case "auth/email-already-in-use":
        throw new Error(
          "This email is already registered. Please log in or use another email."
        );
      case "auth/invalid-email":
        throw new Error("Invalid email format.");
      case "auth/weak-password":
        throw new Error(
          "Your password is too weak. Please use a stronger password."
        );
      default:
        throw new Error("Registration failed. Please try again.");
    }
  }
};

/**
 * 🔹 Cek apakah email sudah diverifikasi & hapus akun jika tidak diverifikasi dalam 2 menit
 */
export const checkVerificationAndDelete = async (targetUser) => {
  const user = targetUser || auth.currentUser;
  if (!user) return;

  await user.reload();
  if (!user.emailVerified) {
    try {
      if (!auth.currentUser || auth.currentUser.uid !== user.uid) return;
      await deleteUser(user);
      console.log("Account deleted because it was not verified within 2 minutes.");
    } catch (error) {
      console.error("Failed to delete unverified account:", error);
    }
  }
};

/**
 * 🔹 Kirim ulang email verifikasi
 */
export const sendVerificationEmail = async (user) => {
  if (!user) throw new Error("User not found.");
  try {
    await sendEmailVerification(user);
    return "Verification email sent. Please check your inbox.";
  } catch (error) {
    console.error("Error sending verification email:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Cek apakah email sudah diverifikasi
 */
export const checkEmailVerification = async () => {
  if (!auth.currentUser) return false;
  await auth.currentUser.reload();
  return auth.currentUser.emailVerified;
};

/**
 * 🔹 Login dengan email & password
 */
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    if (user) {
      await user.reload();
      if (!user.emailVerified) {
        throw new Error("Your email has not been verified yet. Please check your inbox.");
      }
      await saveUserToFirestore(user);
    }
    return user;
  } catch (error) {
    console.error("Error login:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Login dengan Google
 */
export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    if (user) {
      await user.reload();
      await saveUserToFirestore(user);
      return user;
    }
  } catch (error) {
    console.error("Error login Google:", error.message);
    const errorCode = error.code || "unknown_error";
    switch (errorCode) {
      case "auth/popup-closed-by-user":
        throw new Error("Login was cancelled. Please try again.");
      default:
        throw new Error("Google sign-in failed. Please try again.");
    }
  }
};

/**
 * 🔹 Logout user
 */
export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error("Error logout:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Update nama profil setelah registrasi
 */
export const updateUserProfile = async (name) => {
  try {
    if (!auth.currentUser) throw new Error("User not found.");
    await updateProfile(auth.currentUser, { displayName: name });
  } catch (error) {
    console.error("Error update profile:", error.message);
    throw new Error(error.message);
  }
};

/**
 * 🔹 Refresh user data setelah verifikasi
 */
export const refreshUser = async () => {
  if (auth.currentUser) {
    await auth.currentUser.reload();
    return auth.currentUser;
  }
  return null;
};

/**
 * 🔹 Reset password
 */
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return "Password reset email sent. Please check your inbox.";
  } catch (error) {
    console.error("Error reset password:", error.message);
    const errorCode = error.code || "unknown_error";
    switch (errorCode) {
      case "auth/user-not-found":
        throw new Error("This email is not registered. Please check and try again.");
      case "auth/invalid-email":
        throw new Error("Invalid email format.");
      default:
        throw new Error("Failed to send the reset link. Please try again.");
    }
  }
};
